import { MODE, MODE_LABELS } from './ProductButtonMode';
import { freshUserStatus } from './utils';

/**
 * Class representing a single 'Add to Cart' container instance.
 */
export default class ProductButton {
	/**
	 * Default settings for AJAX calls.
	 * @type {{async: boolean, headers: {'Content-Type': string}, processData: boolean, method: string, xhrFields: {withCredentials: boolean}, dataType: string, crossDomain: boolean}}
	 */
	AJAX_SETTINGS = {
		async: true,
		crossDomain: true,
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		processData: false,
		xhrFields: { withCredentials: true },
		dataType: 'json',
	}

	/**
	 * Enumeration of possible modes for the 'Add to Cart' buttons.
	 * These modes are mutually exclusive.
	 */
	// Strategy implementations for each mode
	DEFAULT_STRATEGIES = {
		[ MODE.unchecked_prompt ]: {
			container: {
				classes: null,
				attributes: null,
				properties: null,
			},
			button: {
				text: 'Add to Cart',
				enabled: false,
				handler: null,
				classes: null,
				attributes: null,
				properties: null,
			},
		},
		[ MODE.unchecked_progress ]: {
			container: {
				classes: null,
				attributes: null,
				properties: null,
			},
			button: {
				text: 'Checking...',
				enabled: false,
				handler: null,
				classes: null,
				attributes: null,
				properties: null,
			},
		},
		[ MODE.sellable_prompt ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Add to Cart',
					enabled: true,
					handler: (e) => this.handleAddToCart(e),
					classes: null,
					attributes: null,
					properties: null,
				},

			},
		[ MODE.sellable_progress ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Adding...',
					enabled: false,
					handler: null,
					classes: null,
					attributes: null,
					properties: null,
				},
			},
		[ MODE.sellable_done ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Go to Checkout',
					enabled: true,
					handler: (e) => this.handleGoToCheckout(e),
					classes: null,
					attributes: null,
					properties: null,
				},

			},
		[ MODE.enrollable_prompt ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Enroll Now',
					enabled: true,
					handler: (e) => this.handleEnroll(e),
					classes: null,
					attributes: null,
					properties: null,
				},
			},
		[ MODE.enrollable_progress ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Enrolling...',
					enabled: false,
					handler: null,
					classes: null,
					attributes: null,
					properties: null,
				},
			},
		[ MODE.enrollable_done ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Go to Dashboard',
					enabled: true,
					handler: (e) => this.handleGoToDashboard(e),
					classes: null,
					attributes: null,
					properties: null,
				},
			},
		[ MODE.waitable_prompt ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Join Waitlist',
					enabled: true,
					handler: (e) => this.handleGoToWaitlist(e),
					classes: null,
					attributes: null,
					properties: null,
				},
			},
		[ MODE.waitable_disabled ]:
			{
				container: {
					classes: null,
					attributes: null,
					properties: null,
				},
				button: {
					text: 'Sold Out',
					enabled: false,
					handler: null,
					classes: { 'sold-out': true },
					attributes: null,
					properties: null,
				},
			},
		[ MODE.unavailable ]: {
			container: {
				classes: null,
				attributes: null,
				properties: {
					upsell: {},
					'is-checking-upsell': null,
				},
			},
			button: {
				text: 'Unavailable',
				enabled: false,
				handler: null,
				classes: {
					'is-invalid': true,
					'is-unknown-state': true,
				},
				attributes: {
					title: 'Unavailable',
				},
				properties: null,
			},
		},
	};

	/**
	 * Create an AddToCartContainer instance.
	 * @param {jQuery} $ctrl - The container element.
	 * @param {string} productCode - The product code.
	 * @param {ProductButtonMediator} mediator - The mediator instance.
	 * @param labels
	 */
	constructor( $ctrl, productCode, mediator, labels = null ) {
		this.user = freshUserStatus();
		this.productCode = productCode;
		this.$ctrl = $ctrl;
		this.$btn = $ctrl.find('button');
		this.productStatus = null;
		this.mode = MODE.unchecked_prompt;
		this.upsell = null;
		this.mediator = mediator;
		this.strategies = this.DEFAULT_STRATEGIES;

		const customLabels = JSON.parse( $ctrl.attr('data-custom-labels'));

		if ( labels && typeof labels === 'object' ) {
			Object.entries( MODE_LABELS ).forEach( ( [mode, label] ) => {
				this.strategies[ mode ].button.text = customLabels[mode] ?? labels[ mode ] ?? label;
			} );
		}

		// Initialize the element
		this.initializeElement();

		// Store a reference to this instance on the DOM element
		this.$ctrl.data('productButtonInstance', this);
	}

	/**
	 * Set the mediator for this colleague.
	 * @param {ProductButtonMediator} mediator - The mediator instance.
	 */
	setMediator( mediator ) {
		this.mediator = mediator;
	}

	/**
	 * Initialize the element by setting initial data and events.
	 */
	initializeElement() {
		this.$ctrl.data( 'status', MODE.unchecked_prompt )
			.on( 'click', 'button', ( e ) => {

				if ( this.$ctrl.data('link-url') ) {
					this.handleGoToLinkUrl(e);
				} else if ( typeof this.strategies?.[ this.mode ]?.button?.handler === 'function' ) {
					this.strategies[ this.mode ].button.handler( e );
				}

				e.preventDefault();
				e.stopPropagation();
			} );
	}

	/**
	 * Receive updates from the mediator.
	 *
	 * @param {Object|null} productStatus - The product status.
	 * @param mode
	 */
	receive( productStatus, mode = null ) {
		this.productStatus = productStatus;

		// If not provided, determine the mode and set the strategy
		this.mode = mode || this.determineMode();

		this.$ctrl.attr( 'data-mode', this.mode );

		/**
		 * TODO externalize this
		 */
		this.notifyAncestors( this.mode );

		const strategy = this.strategies[ this.mode ];
		if ( strategy ) {
			// Update container
			this.applyStrategy( this.$ctrl, strategy.container );

			// Update button
			this.applyStrategy( this.$btn, strategy.button );
		}
	}

	/**
	 * Notify the mediator about a completed action.
	 * @param {string} action - The action type. Should be one of the known "modes"
	 * @param {Object} data - Additional data.
	 */
	notifyMediator( action, data ) {
		if ( this.mediator ) {
			this.mediator.notify(
				this.productCode,
				{
					...this.productStatus,
					...data
				},
				action
			);
		}
	}

	/**
	 * Determine the mode for the button based on product status.
	 * @returns {string} - The mode for the button.
	 */
	determineMode() {
		const productStatus = this.productStatus;

		if (
			productStatus?.isInvalid
		) {
			// Invalid product
			return MODE.unavailable;
		}

		if (
			productStatus?.seatsRemaining !== false
		) {
			if ( parseInt( productStatus.seatsRemaining, 10 ) > 0 ) {
				// Seats available
				return MODE.sellable_prompt;
			} else {
				// No seats available
				if ( this.isWaitlistAllowed() ) {
					// Waitlist allowed
					return MODE.waitable_prompt;
				} else {
					return MODE.waitable_disabled;
				}
			}
		}

		if (
			( this.user?.isLoggedIn === false && this.user?.role !== 'Guest' ) ||
			productStatus === null
		) {
			if ( productStatus?.inCart ) {
				// Product is in cart
				return MODE.sellable_done;
			}

			return MODE.sellable_prompt;
		}

		if (
			this.user?.isLoggedIn &&
			productStatus?.inMembership &&
			this.user?.role === 'Student'
		) {
			// Show zero price for visitors who have membership
			// TODO externalize
			this.updatePriceElements();
		}

		if (
			productStatus?.inMembership &&
			!productStatus?.isEnrolled &&
			!productStatus?.inCart
		) {
			return MODE.enrollable_prompt;
		}

		if ( productStatus?.isEnrolled ) {
			return MODE.enrollable_done;
		}

		if ( productStatus?.inCart ) {
			return MODE.sellable_done;
		}

		return MODE.sellable_prompt;
	}

	/**
	 * Check if waitlist is allowed for the given container.
	 * @returns {boolean} - True if waitlist is allowed, false otherwise.
	 */
	isWaitlistAllowed() {
		//the first part is for backwards-compatibility
		return this.$ctrl.find( '.waitlist-allowed' ).length > 0 || +this.$ctrl.data( 'waitlist-allowed' ) === 1;
	}

	/**
	 * Apply the strategy to the element (container or button).
	 * @param {jQuery} $el - The element to update.
	 * @param {Object} strategy - The strategy object.
	 */
	applyStrategy( $el, strategy ) {
		if ( !strategy ) return;

		// Set text if applicable
		strategy.text !== undefined && $el.text( strategy.text );

		// Enable or disable the element
		strategy.enabled !== undefined && $el.prop( 'disabled', !strategy.enabled );

		// Set `click` handler
		// if ( strategy.handler !== undefined ) {
		// 	$el.off( 'click' );
		// 	strategy.handler && $el.on( 'click', strategy.handler );
		// }

		// Handle classes
		if ( strategy.classes ) {
			if ( typeof strategy.classes === 'string' || Array.isArray( strategy.classes ) ) {
				$el.addClass( Array.isArray( strategy.classes ) ? strategy.classes.join( ' ' ) : strategy.classes );
			} else if ( typeof strategy.classes === 'object' ) {
				Object.entries( strategy.classes ).forEach( ( [className, condition] ) => {
					$el.toggleClass( className, ( typeof condition === 'function' ) ? condition( $el ) : !!condition );
				} );
			}
		}

		// Handle attributes and properties
		['attributes', 'properties'].forEach( type => {
			if ( strategy[ type ] ) {
				Object.entries( strategy[ type ] ).forEach( ( [key, value] ) => {
					const resolvedValue = typeof value === 'function' ? value( $el ) : value;
					resolvedValue === null
						? $el[ type === 'attributes' ? 'removeAttr' : 'removeProp' ]( key )
						: $el[ type === 'attributes' ? 'attr' : 'prop' ]( key, resolvedValue );
				} );
			}
		} );

	}

	/**
	 * Set the mode attribute on the container and its parent elements.
	 * @param {string} mode - The mode to set.
	 */
	notifyAncestors( mode ) {
		const $ctrl = this.$ctrl;

		if ( typeof MODE[ mode ] === 'undefined' ) {
			console.error( 'unknown add to cart button mode', mode );
		}

		/**
		 * Do not merge the following into one, because .quick-view is nested inside .card
		 */
		$ctrl.closest( '.quick-view' ).attr( 'data-product-status', mode );
		$ctrl.closest( '.card' ).attr( 'data-product-status', mode );
	}

	/**
	 * Handle the 'Add to Cart' button click event.
	 * @param {Event|null} e - The event object.
	 */
	handleAddToCart( e = null ) {

		if ( this.mediator.isDebounceNeeded( 'addToCart', this.productCode ) ) return; // Avoid duplicate additions

		const $ctrl = this.$ctrl;

		this.notifyMediator( MODE.sellable_progress );

		this.doAddToCart(
			this.productCode,
			$ctrl.data( 'alt-payment-option-id' ) || $ctrl.data( 'payment-option-id' ) || 0,
			$ctrl.data( 'partnership-group-item-id' ) || 0,
			this?.productStatus?.enrollmentId || 0,
			(query, cart, status) => this.doAfterAddToCart(query, cart, status)
		);

		e && e.preventDefault() && e.stopPropagation();
	}

	/**
	 * Add a product to the cart.
	 * @param {string} productCode - The product code to add to the cart.
	 * @param {number} paymentOptionId - The payment option ID.
	 * @param {number|null} partnershipGroupItemId - The partnership group item ID.
	 * @param {number|null} enrollmentId - The enrollment ID.
	 * @param {Function} callback - The callback function to execute after adding.
	 */
	doAddToCart(
		productCode,
		paymentOptionId,
		partnershipGroupItemId,
		enrollmentId,
		callback
	) {

		// Construct the query parameters dynamically

		const query = {
			searchResultId: productCode,
			...( enrollmentId && { enrollmentId } ),
			...( paymentOptionId && { paymentOptionId } ),
			...( partnershipGroupItemId && { PartnershipGroupItemId: partnershipGroupItemId } )
		}

		const queryParams = new URLSearchParams( query );

		const url = `${window.colibri.studentApi}/cart/items.json?${queryParams}`;

		/**
		 * TODO externalize that
		 */
		//pushAnalyticsData( productCode, 'add_to_cart' );

		jQuery.ajax( {
			...this.AJAX_SETTINGS,
			url,
			success: (cart, status, xhr ) => {
				jQuery('body').trigger( 'ColibriEvent:DO_ADD_TO_CART_SUCCESS_POPUP', [cart] );
				jQuery('body').trigger( 'ColibriEvent:DO_ADD_TO_CART_SUCCESS', [cart] );
				callback?.( query, cart, status ); // Safely invoke callback if defined
			},
			error: ( xhr, status, error ) => {
				callback?.( query, null, status );
			}
		} );
	}

	/**
	 * Callback after doAddToCart completes (successfully or not)
	 *
	 * @typedef {Object} CartItem
	 * @property {number} Id - Unique identifier for the cart item.
	 * @property {number} ShoppingCartID - Identifier for the shopping cart.
	 * @property {number} SubsidiaryID - ID for the subsidiary.
	 * @property {number} CourseID - Identifier for the associated course.
	 * @property {number} CartItemAmount - Amount for the cart item.
	 * @property {string} StateAbbv - State abbreviation (e.g., "CA").
	 * @property {number} LicenseTypeID - Identifier for the license type.
	 * @property {string} LicenseTypeName - Name of the license type.
	 * @property {number} StudentID - Identifier for the student.
	 * @property {string} cartItemDate - Date when the item was added to the cart in serialized date format.
	 * @property {string} XACTN_TS - Timestamp of the transaction in serialized date format.
	 * @property {boolean} Gift - Indicates if the item is a gift.
	 * @property {string} AddedFrom - Source URL or reference for where the item was added from.
	 * @property {boolean} Upsell - Indicates if the item is an upsell offer.
	 * @property {string} url - URL associated with the cart item (if any).
	 * @property {boolean} Invalid - Indicates if the item is invalid.
	 * @property {boolean} AdditionalCertificate - Indicates if the item includes an additional certificate.
	 *
	 * @param {object} query
	 * @param {CartItem[]|null} cart - Array of cart item objects.
	 * @param {string} status
	 */
	doAfterAddToCart( query, cart, status ) {

		console.log( query, cart, status );

		if ( status === 'error' ) {
			/*
			 * Operation failed, `cart` will be null -- bail out early.
			 */
			this.notifyMediator( MODE.unavailable );
			return;
		}

		const $ctrl = this.$ctrl;
		const isImmediateCheckout = + $ctrl.data( 'immediate-checkout' ) === 1;
		const includeProductCode = $ctrl.data('include-product-code') || '';
		const isUpsell = !!includeProductCode
			|| $ctrl.closest( '.membership-upgrade-container' ).length
			|| $ctrl.closest( '.upsell-block' ).length;

		this.user = freshUserStatus();

		window.dispatchEvent(
			new CustomEvent( 'ColibriEvent:ADD_TO_CART_CALLBACK' )
		);

		// Notify the mediator about the cart addition
		if ( !isUpsell ) {
			/*
			 * we only notify the mediator here when this is not an upsell
			 * otherwise we need to wait for the product referenced by includeProductCode to be added
			 */
			this.notifyMediator( MODE.sellable_done, { inCart: true } );
			if ( isImmediateCheckout ) {
				this.handleGoToCheckout();
			}
		} else {
			/*
			 * try to click the CTA for a product that has to be included with this upsell product
			 */
			let $btn2 = $ctrl.parent().parent().siblings( '.add-to-cart-container' ).find( '.add-to-cart button' );
			if ( $btn2.length ) {
				//the back-compat version
				if ( !$btn2.parent().is( ':disabled' ) && !$btn2.is( '.hidden' ) ) {
					$btn2.prop( 'disabled', false ).prop( 'declick', 1 ).click();
				}
				this.notifyMediator( MODE.sellable_done, { inCart: true } );
				if ( isImmediateCheckout ) {
					this.handleGoToCheckout();
				}
			} else {
				$btn2 = jQuery( `div[data-product-code="${includeProductCode}"][data-mode="${MODE.sellable_prompt}"]:visible button:not(:disabled)` );
				if ( $btn2.length ){
					/*
					 * We need to call a specific handler instead of invoking click() function
					 * because we want to be sure that the product gets added to cart
					 * even when the control has an arbitrary url in `data-link-url` attribute.
					 */
					$btn2.parent().data('productButtonInstance').handleAddToCart(null);
				}

				this.notifyMediator( MODE.sellable_done, { inCart: true } );
				if ( isImmediateCheckout ) {
					if ( $btn2.length ) {
						/*
						 * wait until the product referenced by includeProductCode gets added to cart
						 */
						jQuery( 'body' ).on(
							'ColibriEvent:DO_ADD_TO_CART_SUCCESS',
							( event, data ) => {
								this.handleGoToCheckout();
							}
						);
					} else {
						this.handleGoToCheckout();
					}
				}
			}
		}
	}

	/**
	 * Handle the 'Enroll Now' button click event.
	 * @param {Event} e - The event object.
	 */
	handleEnroll( e ) {

		if ( this.mediator.isDebounceNeeded( 'enrollment', this.productCode ) ) return; // Avoid duplicate enrollments

		// Set progress mode on this and all other buttons that share the same product-code
		this.notifyMediator( MODE.enrollable_progress );

		this.doEnroll(
			this.productCode,
			this.$ctrl.data( 'payment-option-id' ) || 0,
			this.$ctrl.data( 'partnership-group-item-id' ) || 0,
			this?.productStatus?.enrollmentId,
			(query, response, status) => this.doAfterEnroll(query, response, status)
		);

		e.preventDefault();
		e.stopPropagation();
	}

	/**
	 * Enroll in a product.
	 * @param {string} productCode - The product code.
	 * @param {number} paymentOptionId - The payment option ID.
	 * @param {number|null} partnershipGroupItemId - The partnership group item ID.
	 * @param {number|null} enrollmentId - The enrollment ID.
	 * @param {Function} callback - The callback function to execute after enrolling.
	 */
	doEnroll(
		productCode,
		paymentOptionId,
		partnershipGroupItemId,
		enrollmentId,
		callback
	) {

		const query = {
			searchResultId: productCode,
			...( enrollmentId && { enrollmentId } ),
			...( paymentOptionId && { paymentOptionId } ),
			...( partnershipGroupItemId && { partnershipGroupItemId } )
		};

		// Construct query parameters dynamically
		const queryParams = new URLSearchParams( query );

		const url = `${window.colibri.studentApi}/my/enroll.json?${queryParams}`;

		/**
		 * TODO externalize that
		 */
		//pushAnalyticsData( productCode, 'add_to_cart' );

		jQuery.ajax( {
			...this.AJAX_SETTINGS,
			url,
			success: (response, status, xhr ) => {
				jQuery('body').trigger( 'ColibriEvent:DO_ENROLL_SUCCESS', [response] );
				callback?.( query, response, status ); // Safely invoke callback if defined
			},
			error: ( xhr, status, error ) => {
				callback?.( query, null, status );
			}
		} );

	}

	/**
	 * @param query
	 * @param response
	 * @param status
	 */
	doAfterEnroll(query, response, status){
		this.user = freshUserStatus();
		// Notify the mediator about the enrollment
		if ( response && status !== 'error') {
			this.notifyMediator( MODE.enrollable_done, { isEnrolled: true } );
		} else {
			this.notifyMediator( MODE.unavailable );
		}
	}

	/**
	 * Update the price elements to show zero price for members.
	 */
	updatePriceElements() {
		const $ctrl = this.$ctrl.closest( '.card' );
		$ctrl.find( '.regular-price' ).text( '$0.00' );
		$ctrl.find( '.pricing-container' ).addClass( 'is-member' );
		$ctrl.find( '.has-discount' ).removeClass( 'has-discount' );
		$ctrl.find( '.has-special-price' ).removeClass( 'has-special-price' );
	}

	/**
	 * Handle the default link URL button click event.
	 * @param {Event} e - The event object.
	 */
	handleGoToLinkUrl( e ) {

		if ( this.mediator.isDebounceNeeded('navigation', this.productCode ) ) return;

		// Redirect to the checkout page
		if ( this.$ctrl.data( 'link-url' ) ) {
			window.location.href = this.$ctrl.data( 'link-url' );
		}
		e.preventDefault();
		e.stopPropagation();
	}

	/**
	 * Handle the 'Go to Checkout' button click event.
	 *
	 * @param {Event} e - The optional event object.
	 */
	handleGoToCheckout( e = null ) {
		// Redirect to the checkout page

		if ( this.mediator.isDebounceNeeded('checkout') ) return;

		if ( window?.colibri?.cartUrl ) {
			window.location.href = window.colibri.cartUrl;
		}
		e && e.preventDefault() && e.stopPropagation();
	}

	/**
	 * Handle the 'Go to Dashboard' button click event.
	 * @param {Event} e - The event object.
	 */
	handleGoToDashboard( e ) {

		if ( this.mediator.isDebounceNeeded('dashboard') ) return;

		// Redirect to the dashboard page
		if ( window?.colibri?.accountUrl ) {
			window.location.href = window.colibri.accountUrl;
		}
		e.preventDefault();
		e.stopPropagation();
	}

	/**
	 * Handle the 'Join Waitlist' button click event.
	 * @param {Event} e - The event object.
	 */
	handleGoToWaitlist( e ) {

		if ( this.mediator.isDebounceNeeded('waitlist') ) return;

		//TODO properly implement this
		// if ( window?.colibri?.waitlistUrl ) {
		// 	window.location.href = window.colibri.waitlistUrl;
		// }
		//this.notifyMediator( MODE.waitable_prompt, { waitlisted: true } );

		e.preventDefault();
		e.stopPropagation();
	}

	/**
	 * Destroy this container instance and clean up.
	 */
	destroy() {
		this.$ctrl.off( 'click', 'button' );
		this.$ctrl.removeData( 'is-initialized' );
		this.$ctrl.removeData( 'productButtonInstance' );

		// Remove from mediator
		if ( this.mediator ) {
			this.mediator.unregister( this.productCode, this );
		}
	}
}
